import { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import {
  addNewcrmAPI,
  fetchAvailableOnboardingsAPI,
  fetchSalesUsersAPI,
  updatecrmAPI,
} from "../../../utils/api/crm";
import {
  crmStatusOptions,
  dateStatusOptions,
  deepMerge,
  emptyForm,
  setNestedValue,
} from "../../../utils/crmExcel";
import { translations } from "../../../utils/lang";
import { Button, Checkbox, Input, Select } from "../../ui";
import "./CRMForm.css";

const Section = ({ title, children }) => (
  <div className="section">
    <h3 className="section-title">{title}</h3>
    {children}
  </div>
);
const fields = {
  overview: [
    ["businessName", "Business name"],
    ["city", "City"],
    ["branchCount", "Branch count", "number"],
    ["ownerName", "Owner name"],
    ["contactNumber", "Owner phone"],
    ["email", "Owner email"],
  ],
  contact: [
    ["jobTitle", "Job title", "select"],
    ["contactName", "Contact name"],
    ["mobile", "Mobile"],
    ["email", "Contact email"],
  ],
  entity: [
    ["entityName", "Entity name"],
    ["commercialRegistration", "CR number"],
    ["crExpiration", "CR expiration", "date"],
    ["vatNumber", "VAT number"],
  ],
};

/*
  A CRM lead: its sales state plus its business profile. The profile is written to the lead's STORE (a client that registered) or to the
  lead's own prospect profile (no account yet); the old onboarding workflow (documents, quotation, receipts) no longer exists here -
  subscriptions and payments are in Billing, the client's documents in their dashboard.
*/
export default function CRMForm({ mode, initialData, onClose, onRefresh }) {
  const { handleTokenExpiration, lang } = useContext(Context);
  const { can } = useAccess();
  const [form, setForm] = useState(emptyForm);
  const [creationMode, setCreationMode] = useState("new");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]); // existing clients (stores) that have no lead yet
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    Promise.all([
      fetchSalesUsersAPI(token),
      mode === "create"
        ? fetchAvailableOnboardingsAPI(token)
        : Promise.resolve([]),
    ]).then(([sales, available]) => {
      setUsers(sales || []);
      setClients(available || []);
    });
  }, [mode]);
  useEffect(
    () =>
      setForm(
        initialData
          ? deepMerge(emptyForm, initialData)
          : deepMerge(emptyForm, {}),
      ),
    [initialData],
  );
  const change = (path, value) =>
    setForm((previous) => setNestedValue(previous, path, value));
  const optionLabels = (items) =>
    items.map(({ value, label }) => ({
      value,
      label: typeof label === "string" ? label : label?.[lang] || "",
    }));
  const available = useMemo(
    () =>
      clients.filter(
        (item) =>
          !search.trim() ||
          [
            item.store?.businessName,
            item.store?.city,
            item.contact?.contactName,
            item.contact?.email,
          ].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(search.trim().toLowerCase()),
          ),
      ),
    [clients, search],
  );
  const store = form.onboarding?.store || {};
  const contact = form.onboarding?.contact || {};
  const entity = form.onboarding?.entity || {};
  const showCustomer = mode === "edit" || creationMode === "new";

  // Built at the moment of saving, with the permission checked then: the
  // assignee is part of the payload only for a user who may change it
  // (crm.assignee.update), whatever the form state or the control shows (NA-010).
  // Omitted, the server keeps the current assignee.
  const buildCrmData = () => ({
    isDuplicate: Boolean(form.isDuplicate),
    isSelfClient: Boolean(form.isSelfClient),
    crm: {
      status: form.crm?.status,
      followUpEndDate: form.crm?.followUpEndDate || null,
      ...(can("crm.assignee.update")
        ? {
            assignedUser:
              form.crm?.assignedUser?._id || form.crm?.assignedUser || undefined,
          }
        : {}),
      dateStatus: form.crm?.dateStatus,
      isAddedStore: Boolean(form.crm?.isAddedStore),
    },
  });
  const onboardingData = {
    store,
    contact,
    entity,
    documents: { notes: form.onboarding?.documents?.notes },
  };

  const titleOptions = [
    { value: "محاسب", label: lang === "ar" ? "محاسب" : "Accountant" },
    { value: "مدير فرع", label: lang === "ar" ? "مدير فرع" : "Branch Manager" },
    {
      value: "مدير تشغيل",
      label: lang === "ar" ? "مدير تشغيل" : "Operations Manager",
    },
    { value: "أخرى", label: lang === "ar" ? "أخرى" : "Other" },
  ];
  const save = async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setSaving(true);
    setError("");
    try {
      const result =
        mode === "create"
          ? await addNewcrmAPI(
              token,
              creationMode === "existing"
                ? {
                    mode: "existing",
                    onboardingId: form.onboarding?._id,
                    crmData: buildCrmData(),
                  }
                : { mode: "new", crmData: buildCrmData(), onboardingData },
            )
          : await updatecrmAPI(token, {
              id: form._id,
              payload: { crmData: buildCrmData(), onboardingData },
            });
      if (result) {
        onRefresh(result);
        onClose();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const invalid =
    saving ||
    form.migrationStatus === "unresolved" ||
    (mode === "create" &&
      creationMode === "existing" &&
      !form.onboarding?._id) ||
    (mode === "create" &&
      creationMode === "new" &&
      (!store.businessName?.trim() ||
        !(contact.email?.trim() || store.email?.trim())));
  const renderFields = (group, base, values) =>
    fields[group].map(([key, label, type]) =>
      type === "select" ? (
        <Select
          key={key}
          value={values[key] ?? ""}
          label={lang === "ar" ? "المسمى الوظيفي" : "Job Title"}
          placeholder={lang === "ar" ? "اختر" : "Select"}
          options={titleOptions}
          onChange={(e) => change(`${base}.${key}`, e.target.value)}
        />
      ) : (
        <Input
          key={key}
          label={label}
          type={type}
          value={
            type === "date"
              ? values[key]?.slice?.(0, 10) || ""
              : (values[key] ?? "")
          }
          onChange={(e) =>
            change(
              `${base}.${key}`,
              type === "number" ? Number(e.target.value) : e.target.value,
            )
          }
        />
      ),
    );
  return (
    <div className="crm-form">
      {mode === "create" && (
        <div className="crm-create-choice">
          <Button
            type="button"
            variant="primary"
            appearance={creationMode === "new" ? "filled" : "soft"}
            onClick={() => {
              setCreationMode("new");
              setForm(deepMerge(emptyForm, {}));
            }}
          >
            {lang === "ar" ? "+ عميل جديد" : "+ New Customer"}
          </Button>
          <Button
            type="button"
            variant="primary"
            appearance={creationMode === "existing" ? "filled" : "soft"}
            onClick={() => {
              setCreationMode("existing");
              setForm(deepMerge(emptyForm, {}));
            }}
          >
            {lang === "ar" ? "ربط عميل حالي" : "Link Existing Client"}
          </Button>
        </div>
      )}
      {form.migrationStatus === "unresolved" && (
        <p className="crm-form-error">
          This legacy CRM is preserved but has no business profile; add one by editing it.
        </p>
      )}
      {error && <p className="crm-form-error">{error}</p>}
      {mode === "create" && creationMode === "existing" && (
        <Section title={lang === "ar" ? "العميل الحالي" : "Existing client"}>
          <Input
            label={lang === "ar" ? "البحث عن العملاء" : "Search customers"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={form.onboarding?._id || ""}
            options={available.map((item) => ({
              value: item._id,
              label: `${item.store?.businessName || "Unnamed"} — ${item.store?.city || "No city"} — ${item.contact?.email || item.store?.email || "No email"}`,
            }))}
            onChange={(e) => {
              const selected = clients.find(
                (item) => item._id === e.target.value,
              );
              if (selected)
                setForm((current) => ({ ...current, onboarding: selected }));
            }}
          />
        </Section>
      )}
      <div className="crm-form-grid">
        {showCustomer && (
          <>
            <Section title="Customer Overview">
              {renderFields("overview", "onboarding.store", store)}
            </Section>
            <Section title="Contact Information">
              {renderFields("contact", "onboarding.contact", contact)}
            </Section>
            <Section title="Business / Entity Information">
              {renderFields("entity", "onboarding.entity", entity)}
              <Input
                textarea
                label={lang === "ar" ? "ملاحظات" : "Notes"}
                className="confirm-input"
                value={form.onboarding?.documents?.notes || ""}
                onChange={(e) =>
                  change("onboarding.documents.notes", e.target.value)
                }
              />
            </Section>
          </>
        )}
        <Section title="CRM Management">
          <Select
            label="Status"
            value={form.crm?.status || "new"}
            options={optionLabels(crmStatusOptions)}
            onChange={(e) => change("crm.status", e.target.value)}
          />
          <Select
            label="Date status"
            value={form.crm?.dateStatus || "asap"}
            options={optionLabels(dateStatusOptions)}
            onChange={(e) => change("crm.dateStatus", e.target.value)}
          />
          <Input
            label="Follow-up"
            type="datetime-local"
            value={form.crm?.followUpEndDate || ""}
            onChange={(e) => change("crm.followUpEndDate", e.target.value)}
          />
          {can("crm.assignee.update") && (
            <Select
              label={lang === "ar" ? "الموظف المسؤول" : "Assigned user"}
              value={
                form.crm?.assignedUser?._id || form.crm?.assignedUser || ""
              }
              options={users.map((user) => ({
                value: user._id,
                label: user.name,
              }))}
              onChange={(e) => change("crm.assignedUser", e.target.value)}
            />
          )}
          <Checkbox
            label="Duplicate"
            checked={Boolean(form.isDuplicate)}
            onChange={(e) => change("isDuplicate", e.target.checked)}
          />
          <Checkbox
            label="Self client"
            checked={Boolean(form.isSelfClient)}
            disabled={mode === "create" && creationMode === "new"}
            onChange={(e) => change("isSelfClient", e.target.checked)}
          />
          <Checkbox
            label="Store added"
            checked={Boolean(form.crm?.isAddedStore)}
            onChange={(e) => change("crm.isAddedStore", e.target.checked)}
          />
        </Section>
      </div>
      <div className="crm-form-actions">
        <Button
          type="button"
          variant="neutral"
          appearance="soft"
          size="md"
          onClick={onClose}
        >
          {lang === "ar" ? "إلغاء" : "Cancel"}
        </Button>
        <Button
          type="button"
          variant="primary"
          appearance="filled"
          size="md"
          className="notification-btn"
          disabled={invalid}
          loading={saving}
          onClick={save}
        >
          {saving
            ? lang === "ar"
              ? "جاري الإرسال..."
              : "Submitting..."
            : mode === "create"
              ? translations[lang].crm.form.create
              : translations[lang].crm.form.edit}
        </Button>
      </div>
    </div>
  );
}
